import React, { useState } from 'react';
import { View, ScrollView, TouchableOpacity, Alert, StyleSheet, Switch, Linking, Share, Platform, Modal, TextInput } from 'react-native';
import { Text } from '@/components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, ChevronRight, Star, Share2, Mail, FileText } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useDogs } from '@/context/DogsContext';
import styles from './settings.styles';
import theme from '@/constants/colors';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/lib/api';
import LottieLoader from '@/components/LottieLoader';
import Constants from 'expo-constants';
import { useTranslation } from 'react-i18next';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';

export default function SettingsScreen() {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { t } = useTranslation();
  const auth = useAuth();
  const { dogProfile } = useDogs();
  const [showLoader, setShowLoader] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useAnalyticsConsent();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  const handleRow = (label: string, path?: string) => {
    if (path) {
      router.push(path as any);
      return;
    }
    Alert.alert(label);
  };

  // Apple ID 6758676111 = "TailTrails - Life Unleashed" (com.tailtrails.app), via the iTunes Search API.
  const APPLE_ID = '6758676111';
  const IOS_STORE_WEB = `https://apps.apple.com/app/id${APPLE_ID}`;
  // Rate: deep-link straight into the store app (Android Play, iOS App Store write-review).
  const ANDROID_RATE_URL = 'market://details?id=com.tailtrailsclub.app';
  const IOS_RATE_URL = `itms-apps://itunes.apple.com/app/id${APPLE_ID}?action=write-review`;
  // Share: plain https links so the message is openable on any device / web (itms-apps://
  // and market:// schemes only work on their own platform).
  const SHARE_URL = Platform.OS === 'android'
    ? 'https://play.google.com/store/apps/details?id=com.tailtrailsclub.app'
    : IOS_STORE_WEB;
  const TERMS_URL = 'https://tailtrails.club/terms';

  const openUrl = (url: string) => Linking.openURL(url).catch((e) => console.warn('openURL failed', e));

  const handleRateApp = async () => {
    // iOS: the itms-apps:// deep-link opens the App Store write-review sheet on a real
    // device but rejects on the Simulator (no App Store app). Fall back to the web
    // listing — Universal-Links into the App Store on a device, opens Safari on the
    // Simulator — so "Rate App" always does something visible.
    if (Platform.OS === 'android') { openUrl(ANDROID_RATE_URL); return; }
    try {
      await Linking.openURL(IOS_RATE_URL);
    } catch {
      await Linking.openURL(IOS_STORE_WEB).catch((e) => console.warn('open App Store failed', e));
    }
  };
  const handleShareApp = async () => {
    try { await Share.share({ message: `${t('settings.shareAppMessage')} ${SHARE_URL}` }); }
    catch (e) { /* user cancelled */ }
  };
  const handleContactSupport = () => setShowFeedback(true);
  const submitFeedback = async () => {
    const message = feedbackMessage.trim();
    if (!message) { Alert.alert(t('common.error'), t('settings.feedbackEmpty')); return; }
    setFeedbackSending(true);
    try {
      const token = auth?.session?.accessToken;
      const tokenType = auth?.session?.tokenType || 'Bearer';
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `${tokenType} ${token}`;
      const res = await fetch(`${API_URL}/feedback?message=${encodeURIComponent(message)}`, { method: 'POST', headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to send feedback (${res.status})`);
      }
      setFeedbackMessage('');
      setShowFeedback(false);
      Alert.alert(t('common.success'), t('settings.feedbackSent'));
    } catch (err: any) {
      console.error('Feedback failed', err);
      Alert.alert(t('common.error'), err?.message || t('settings.feedbackFailed'));
    } finally {
      setFeedbackSending(false);
    }
  };
  const handleTerms = () => openUrl(TERMS_URL);

  const handleLogout = async () => {
    try {
      if (auth?.signOut) {
        await auth.signOut();
      }
    } catch (err) {
      console.error('Logout failed', err);
      Alert.alert(t('common.error'), t('profile.failedToSignOut'));
    }
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      t('settings.deleteAccount'),
      t('settings.deleteAccountConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              setShowLoader(true);
              const token = auth?.session?.accessToken;
              const tokenType = auth?.session?.tokenType || 'Bearer';
              if (!token) throw new Error('Not authenticated');

              const res = await fetch(`${API_URL}/account/authenticated/me`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `${tokenType} ${token}`,
                },
              });

              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to delete account');
              }

              // Successful deletion — clear local session and navigate home
              if (auth?.signOut) await auth.signOut();
              Alert.alert(t('settings.accountDeleted'), t('settings.accountDeletedMessage'));
              router.replace('/');
            } catch (err: any) {
              console.error('Delete account failed', err);
              Alert.alert(t('common.error'), err?.message || t('settings.failedToDeleteAccount'));
            } finally {
              setShowLoader(false);
            }
          },
        },
      ],
      { cancelable: true }
    );
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.push('/profile')}>
          <ArrowLeft size={20} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('settings.title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>{t('settings.yourAccount')}</Text>
          <TouchableOpacity style={styles.row} onPress={() => handleRow(t('settings.accountInformation'), '/settings/account')}>
            <Text style={styles.rowText}>{t('settings.accountInformation')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.row} onPress={() => handleRow(t('settings.privacyAndSafety'), '/settings/privacy')}>
            <Text style={styles.rowText}>{t('settings.privacyAndSafety')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          {/* Analytics consent toggle. Required by privacy policy (withdrawable in settings). */}
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowText}>{t('settings.analytics')}</Text>
              <Text style={{ color: theme.textMuted, fontSize: 12, marginTop: 2 }}>{t('settings.analyticsDescription')}</Text>
            </View>
            <Switch
              value={analyticsEnabled}
              onValueChange={setAnalyticsEnabled}
              trackColor={{ false: theme.borderSubtle, true: theme.accentPrimary }}
            />
          </View>
        </View>

        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>{t('settings.trackingAndBattery')}</Text>
          <TouchableOpacity style={styles.row} onPress={() => router.push('/settings/tracking' as any)}>
            <Text style={styles.rowText}>{t('settings.trackingSettings')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.row} onPress={() => router.push('/settings/permissions' as any)}>
            <Text style={styles.rowText}>{t('settings.permissions')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          {/* Dev/QA-only — hidden in production builds. ponytail: gate dev tools with __DEV__, add a remote-feature-flag if it ever needs toggling in prod. */}
          {__DEV__ && (
            <TouchableOpacity style={styles.row} onPress={() => router.push('/settings/notification-debug' as any)}>
              <Text style={styles.rowText}>{t('settings.testNotifications')}</Text>
              <ChevronRight size={18} color={theme.accentPrimary} />
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>{t('settings.dogAccount')}</Text>
          <TouchableOpacity style={styles.row} onPress={() => router.push({ pathname: '/onboarding/dog-profile', params: { from: 'settings' } } as any)}>
            <Text style={styles.rowText}>{t('settings.dogProfile')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          {/* <TouchableOpacity
            style={styles.row}
            onPress={() => {
              // Dog traits are now auto-derived from breed selection
              // on the Dog Profile page, so redirect there
              router.push({ pathname: '/onboarding/dog-profile', params: { from: 'settings' } } as any);
            }}
          >
            <Text style={styles.rowText}>{t('settings.dogTraits')}</Text>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity> */}
        </View>

        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>{t('settings.supportLegal')}</Text>
          <TouchableOpacity style={styles.row} onPress={handleRateApp}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
              <Star size={18} color={theme.accentPrimary} />
              <Text style={styles.rowText}>{t('settings.rateApp')}</Text>
            </View>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.row} onPress={handleShareApp}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
              <Share2 size={18} color={theme.accentPrimary} />
              <Text style={styles.rowText}>{t('settings.shareApp')}</Text>
            </View>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.row} onPress={handleContactSupport}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
              <Mail size={18} color={theme.accentPrimary} />
              <Text style={styles.rowText}>{t('settings.contactSupport')}</Text>
            </View>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.row} onPress={handleTerms}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
              <FileText size={18} color={theme.accentPrimary} />
              <Text style={styles.rowText}>{t('settings.termsAndConditions')}</Text>
            </View>
            <ChevronRight size={18} color={theme.accentPrimary} />
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={styles.logoutCard} onPress={handleLogout}>
          <Text style={styles.logoutText}>{t('settings.logout')}</Text>
          <ChevronRight size={18} color={theme.accentPrimary} />
        </TouchableOpacity>

        <TouchableOpacity style={styles.deleteButton} onPress={handleDeleteAccount}>
          <Text style={styles.deleteText}>{t('settings.deleteAccount')}</Text>
        </TouchableOpacity>

        {/* Version label — easy for testers to confirm which build they're on */}
        <Text style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', marginTop: 24, marginBottom: 16 }}>
          v{Constants.expoConfig?.version ?? '?'}
        </Text>
      </ScrollView>

      {showLoader && (
        <View style={overlayStyles.overlay}>
          <View style={overlayStyles.container}>
            <LottieLoader size={220} />
            <TouchableOpacity style={overlayStyles.close} onPress={() => setShowLoader(false)}>
              <Text style={overlayStyles.closeText}>{t('common.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Contact Support — in-app feedback form posted to POST /feedback */}
      <Modal visible={showFeedback} transparent animationType="fade" onRequestClose={() => setShowFeedback(false)}>
        <View style={overlayStyles.overlay}>
          <View style={overlayStyles.feedbackCard}>
            <Text style={overlayStyles.feedbackTitle}>{t('settings.contactSupport')}</Text>
            <TextInput
              style={overlayStyles.feedbackInput}
              value={feedbackMessage}
              onChangeText={setFeedbackMessage}
              placeholder={t('settings.feedbackPlaceholder')}
              placeholderTextColor={theme.textMuted}
              multiline
              autoFocus
              editable={!feedbackSending}
            />
            <View style={overlayStyles.feedbackActions}>
              <TouchableOpacity style={overlayStyles.feedbackCancel} onPress={() => setShowFeedback(false)} disabled={feedbackSending}>
                <Text style={overlayStyles.feedbackCancelText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={overlayStyles.feedbackSend} onPress={submitFeedback} disabled={feedbackSending}>
                <Text style={overlayStyles.feedbackSendText}>{feedbackSending ? t('settings.feedbackSending') : t('common.send')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const overlayStyles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999,
  },
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  close: {
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
  },
  closeText: {
    color: '#111827',
    fontWeight: '600',
  },
  feedbackCard: {
    width: '88%',
    maxWidth: 360,
    backgroundColor: theme.backgroundPrimary,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  feedbackTitle: {
    color: theme.textPrimary,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
  },
  feedbackInput: {
    minHeight: 120,
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 12,
    padding: 12,
    color: theme.textPrimary,
    fontSize: 15,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  feedbackActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 14,
  },
  feedbackCancel: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: theme.backgroundSecondaryVarient,
  },
  feedbackCancelText: {
    color: theme.textSecondary,
    fontWeight: '600',
  },
  feedbackSend: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: theme.accentPrimary,
  },
  feedbackSendText: {
    color: theme.backgroundPrimary,
    fontWeight: '700',
  },
});
